import { safeLocalStorage } from '@/lib/safeStorage';
import { attemptChunkReload, isChunkReloadInFlight } from '@/lib/chunkReload';
import React, { Suspense } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { PostHogProvider } from 'posthog-js/react';
import { AnimatedOutlet } from './components/AnimatedOutlet';
import Login from './pages/auth/Login';
import ConfirmEmail from './pages/auth/ConfirmEmail';
import ResetPassword from './pages/auth/ResetPassword';
import { CircleNotch, X } from '@phosphor-icons/react';

// Auto-reload once when a route chunk fails to load (stale deploy). The guard
// and the reload itself live in lib/chunkReload so the non-route dynamic
// imports, caught by the vite:preloadError listener in main.tsx, cannot reload
// a second time for the same failure (RUT-2928, RUT-3216).
function lazyWithRetry(importFn: () => Promise<any>) {
  return React.lazy(() =>
    importFn().catch((error: any) => {
      // Either we start the reload, or the vite:preloadError listener already
      // did for this same failure — both mean the page is on its way out, so
      // stay suspended on the spinner instead of flashing an error.
      if (attemptChunkReload() || isChunkReloadInFlight()) {
        return new Promise(() => {}); // never resolves — page is reloading
      }
      throw error;
    })
  );
}

// Lazy-loaded authenticated pages
const Onboarding = lazyWithRetry(() => import('./pages/company/Onboarding'));
const ServiceTemplates = lazyWithRetry(() => import('./pages/company/ServiceTemplates'));
const ServiceTemplateDetailsPage = lazyWithRetry(() => import('./pages/company/ServiceTemplateDetailsPage'));
const MergeTemplates = lazyWithRetry(() => import('./pages/company/MergeTemplates'));
const Settings = lazyWithRetry(() => import('./pages/company/Settings'));
const UserSettings = lazyWithRetry(() => import('./pages/company/UserSettings'));
const ViewAsSession = lazyWithRetry(() => import('./pages/auth/ViewAsSession'));
const AuthCallback = lazyWithRetry(() => import('./pages/auth/AuthCallback'));
const CustomersManagement = lazyWithRetry(() => import('./components/customers/CustomersManagement').then(m => ({ default: m.CustomersManagement })));
const CustomerDetails = lazyWithRetry(() => import('./pages/company/CustomerDetails'));
const Team = lazyWithRetry(() => import('./pages/company/Team'));
const Messages = lazyWithRetry(() => import('./pages/company/Messages'));
const Campaigns = lazyWithRetry(() => import('./pages/company/Campaigns'));
const ServicesManagement = lazyWithRetry(() => import('./pages/company/ServicesManagement'));
const InvoiceView = lazyWithRetry(() => import('./pages/company/InvoiceView').then(m => ({ default: m.InvoiceView })));
const InvoiceRouteOverlay = lazyWithRetry(() => import('./components/invoices/InvoiceRouteOverlay'));
const VisitsRequiringReview = lazyWithRetry(() => import('./components/visits/VisitsRequiringReview'));
const VisitsRequiringFollowup = lazyWithRetry(() => import('./components/visits/VisitsRequiringFollowup'));
const AllVisits = lazyWithRetry(() => import('./pages/company/AllVisits'));
const Review = lazyWithRetry(() => import('./pages/company/Review'));
const Dispatch = lazyWithRetry(() => import('./pages/company/Dispatch'));
const RevenueReport = lazyWithRetry(() => import('./pages/company/RevenueReport'));
const PerformanceReport = lazyWithRetry(() => import('./pages/company/PerformanceReport'));
const SubcontractorsReport = lazyWithRetry(() => import('./pages/company/SubcontractorsReport'));
const BillingIssues = lazyWithRetry(() => import('./pages/company/BillingIssues'));
const Home = lazyWithRetry(() => import('./pages/company/Home'));
const Map = lazyWithRetry(() => import('./pages/company/Map'));
const ServiceArea = lazyWithRetry(() => import('./pages/company/ServiceArea'));

import { EntityPathRedirect } from './components/EntityPathRedirect';
import { VisitRedirect } from './components/VisitRedirect';
import { SubscriptionRedirect } from './components/SubscriptionRedirect';
import { useAuthStatus } from './hooks/useAuthStatus';
import { environmentBanner, resolveAppEnvironment } from './lib/appEnvironment';

function useApiEnvironment(): string | null {
  const [apiEnv, setApiEnv] = React.useState<string | null>(null);
  React.useEffect(() => {
    const apiUrl = import.meta.env.VITE_API_URL;
    if (!apiUrl) return;
    fetch(`${apiUrl}/health`).then(r => r.json()).then(data => {
      if (data.environment) setApiEnv(data.environment);
    }).catch(() => {});
  }, []);
  return apiEnv;
}

// Visible on dev/staging builds, and on production builds only when the API
// reports data from a different environment (override). Returns null when no
// banner should render (the normal production case).
function useEnvironmentBanner(): { label: string; colors: string } | null {
  const env = resolveAppEnvironment(window.location.hostname, import.meta.env.VITE_ENVIRONMENT);
  const apiEnv = useApiEnvironment();
  return environmentBanner(env, apiEnv);
}
import { RoleBasedRouter } from './components/RoleBasedRouter';
import { useOrganization } from './hooks/useQueries';
import { SessionExpiredProvider } from './contexts/SessionExpiredContext';
import { SessionExpiredModal } from './components/SessionExpiredModal';
import { Toaster } from './components/ui/toaster';
import { TrialEndedGate } from './components/settings/TrialEndedGate';
import { isLockedAfterPlatformTrial } from '@ruta/shared';
import { ThemeProvider } from './contexts/ThemeContext';
import { BreadcrumbProvider } from './contexts/BreadcrumbContext';
import { RightSidebarProvider, useRightSidebar } from './hooks/useRightSidebar';
import { TopBarActionsProvider } from './hooks/useTopBarActions';
import { ChatProvider } from './contexts/ChatContext';
import { RutaAiProvider } from './contexts/RutaAiContext';
import { RutaAiPopover } from './components/dashboard/live-ops/RutaAiPopover';
import { SetupIntroGate } from './components/ai-elements/SetupIntroGate';
import { SidebarNav } from './components/navigation/SidebarNav';
import { AppChromeHeader } from './components/navigation/AppChromeHeader';
import { ProvisioningBanner } from './components/ProvisioningBanner';
import { ViewAsBanner } from './components/ViewAsBanner';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { STORAGE_KEYS } from './lib/constants';
import { useTokenRefresh } from './lib/api/tokenRefresh';
import { usePostHogIdentify } from './hooks/usePostHogIdentify';
import { appSuperProperties } from './services/analytics';
import { REPLAY_INIT_OPTIONS, syncOnboardingReplay } from './lib/onboardingReplay';
import { useUserInfo } from './hooks/useUserInfo';
import { cn } from './lib/utils';
import { useWebSocket } from './hooks/useWebSocket';
import { useVersionCheck } from './hooks/useVersionCheck';
import { destroyDesktopSharedMap } from './lib/mapbox/desktopSharedMap';
import { FirstRunTour } from './components/onboarding/FirstRunTour';

// App Layout Component that combines sidebar and content area
const AppLayoutWithSidebar = () => {
  const { isOpen, content, title, actions: sidebarActions, toggle } = useRightSidebar();
  const userInfo = useUserInfo();

  // Initialize WebSocket for real-time notification updates
  // This connects when authenticated and VITE_WS_URL is configured
  useWebSocket();

  // One desktop Live/Map canvas — drop it when the signed-in shell unmounts
  // so a later login does not inherit a leaked Mapbox instance (RUT-4535).
  React.useEffect(() => () => destroyDesktopSharedMap(), []);

  // Preload frequently-visited routes on idle to avoid loading flash
  React.useEffect(() => {
    const preload = () => {
      // Swallow failures on purpose. These are speculative warm-ups, not a
      // navigation — if a chunk is missing the user has not asked for it yet,
      // and the route's own lazyWithRetry handles it properly when they do.
      // Unhandled, these surfaced as stack-less unhandled rejections and opened
      // a second Sentry group for a problem already tracked (RUT-3216).
      void import('./pages/company/Home').catch(() => {});
      void import('./pages/company/Map').catch(() => {});
    };
    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(preload);
      return () => cancelIdleCallback(id);
    } else {
      const id = setTimeout(preload, 2000);
      return () => clearTimeout(id);
    }
  }, []);

  // Memoize organizationData to prevent infinite re-renders
  const organizationData = React.useMemo(() => ({
    name: userInfo.organizationName,
    orgId: userInfo.orgId,
    slug: userInfo.organizationSlug
  }), [userInfo.organizationName, userInfo.orgId, userInfo.organizationSlug]);

  return (
    <SidebarProvider
      className="relative min-h-0 h-full"
      style={{
        "--sidebar-width": "14rem",
        "--sidebar-width-mobile": "75vw",
      } as React.CSSProperties}
    >
      <SidebarNav organizationData={organizationData} />
      <SidebarInset className="min-w-0 max-w-full flex flex-col h-full min-h-0 bg-nav">
        {/* Operator "view as" bar (RUT-3105). Renders nothing for ordinary logins. */}
        <ViewAsBanner />

        {/* Where the payment-processing request stands. Renders nothing once it's approved. */}
        <ProvisioningBanner />

        {/* Guided first visit after setup (RUT-3782). Renders nothing unless it was asked for. */}
        <FirstRunTour orgId={userInfo.orgId ?? undefined} />

        {/* Main content: persistent utilities overlay (outside AnimatedOutlet) so
            the top-right chrome never remounts; page title still lives in PageHeader
            on the same visual row. */}
        <div className={cn(
          "relative flex-1 flex flex-col overflow-clip overscroll-none min-h-0 min-w-0",
          content && "lg:mr-80" // Reserve space for right sidebar
        )}>
          <AppChromeHeader />
          <Suspense fallback={<div className="flex flex-1 items-center justify-center bg-stone-50"><CircleNotch className="h-6 w-6 animate-spin text-stone-400" /></div>}>
            <AnimatedOutlet />
          </Suspense>
        </div>
      </SidebarInset>

      {/* Right Sidebar - Fixed position */}
      {content && (
        <>
          {/* Desktop sidebar - dark background matching navbar */}
          <aside className="hidden lg:block absolute right-0 top-0 bottom-0 w-80 bg-nav border-l border-white/5 shadow-xl z-10 flex flex-col">
            {title && (
              <div className="p-4 pb-3 border-b border-white/10 text-right">
                {typeof title === 'string' ? (
                  <h2 className="text-lg font-semibold text-white">{title}</h2>
                ) : (
                  title
                )}
              </div>
            )}
            {sidebarActions && (
              <div className="p-4 pb-0">
                {sidebarActions}
              </div>
            )}
            <div className="flex-1 overflow-y-auto p-4">
              {content}
            </div>
          </aside>

          {/* Mobile overlay */}
          {isOpen && (
            <div className="lg:hidden fixed inset-0 z-50 bg-background/80 backdrop-blur-sm" onClick={toggle}>
              <div className="fixed right-0 top-0 h-full w-80 bg-nav shadow-lg flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                  {title && (
                    <div className="flex-1 text-right mr-4">
                      {typeof title === 'string' ? (
                        <h2 className="text-lg font-semibold text-white">{title}</h2>
                      ) : (
                        title
                      )}
                    </div>
                  )}
                  <button
                    onClick={toggle}
                    className="p-1 hover:bg-white/10 rounded-sm transition-colors flex-shrink-0"
                  >
                    <X className="h-4 w-4 text-white" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                  {content}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </SidebarProvider>
  );
};

function AppContent() {
  const location = useLocation();
  const { isAuthenticated, inOrg, loading: authLoading, orgId } = useAuthStatus();

  // The operator "view as" hand-off (RUT-3105). Stores its tokens and reloads, so it must render
  // before any auth gate — it runs precisely when there is no session yet.
  const isViewAsSession = location.pathname === '/session';
  // Social sign-in return page (RUT-4523). Like /session it creates the
  // session, so it renders before any auth gate.
  const isAuthCallback = location.pathname === '/auth/callback';

  // All authenticated members get full navigation
  const isAdminRoute = isAuthenticated && inOrg;

  const isLogin = location.pathname === '/login' || location.pathname === '/reset-password';
  const isOnboardingPath = location.pathname.startsWith('/onboarding') || location.pathname === '/organization-setup' || location.pathname === '/confirm-email';

  // Determine if we need to check org data for routing decisions
  // Only fetch org data for authenticated users who are in an org and not on public routes
  const needsOrgCheck = isAuthenticated && inOrg && !isLogin && !isOnboardingPath;

  // Check localStorage cache for onboarding status to avoid blocking API call. Read on every
  // render, not once per orgId: the wizard writes this flag the moment setup finishes, and the
  // setup-page redirect already trusts it — a stale value here sent the owner straight back into
  // setup, and the two redirects bounced each other forever.
  const cachedOnboardingComplete =
    !!orgId && safeLocalStorage.getItem(`${STORAGE_KEYS.ONBOARDING_COMPLETED}_${orgId}`) === 'true';

  // Only block on org data fetch if we don't have cached onboarding status
  const shouldBlockForOrgCheck = needsOrgCheck && !cachedOnboardingComplete;

  // Fetch org data - always enabled for authenticated users (for sidebar data)
  // but only blocks initial render if we don't have cached onboarding status
  const { data: orgData, isLoading: isLoadingOrg } = useOrganization({
    enabled: needsOrgCheck
  });

  // Cache onboarding status when we get it from API
  React.useEffect(() => {
    if (orgId && orgData?.organization?.onboardingCompleted === true) {
      safeLocalStorage.setItem(`${STORAGE_KEYS.ONBOARDING_COMPLETED}_${orgId}`, 'true');
    }
  }, [orgId, orgData?.organization?.onboardingCompleted]);

  // View-as hand-off: mount immediately, before any auth/loading gate — it exists to CREATE the
  // session, so it must not wait on one.
  if (isAuthCallback) {
    return (
      <Suspense fallback={<div className="flex min-h-screen items-center justify-center"><CircleNotch className="h-6 w-6 animate-spin text-stone-400" /></div>}>
        <Routes>
          <Route path="/auth/callback" element={<AuthCallback />} />
        </Routes>
      </Suspense>
    );
  }

  if (isViewAsSession) {
    return (
      <Suspense fallback={<div className="flex min-h-screen items-center justify-center"><CircleNotch className="h-6 w-6 animate-spin text-stone-400" /></div>}>
        <Routes>
          <Route path="/session" element={<ViewAsSession />} />
        </Routes>
      </Suspense>
    );
  }

  // Show loading spinner only during auth check or if we need to verify onboarding (no cache)
  if (authLoading || (shouldBlockForOrgCheck && isLoadingOrg)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <CircleNotch className="h-6 w-6 animate-spin text-stone-400" />
      </div>
    );
  }

  // Check if onboarding needs to be completed - redirect before rendering main app
  // Use cached value OR fresh API data
  const isOnboardingComplete = cachedOnboardingComplete || orgData?.organization?.onboardingCompleted === true;
  if (needsOrgCheck && !isOnboardingComplete) {
    return <Navigate to="/onboarding" replace />;
  }

  // Free trial ended and the first charge did not go through: the office app
  // is locked to the plan until it is paid.
  if (needsOrgCheck && orgData?.organization && isLockedAfterPlatformTrial(orgData.organization)) {
    return <TrialEndedGate />;
  }


  // Render public routes (login, onboarding, etc.)
  if (isLogin || isOnboardingPath) {
    return (
      <div className="h-full overflow-auto bg-background">
        <div className="animate-in fade-in duration-200 min-h-full">
          <Suspense fallback={<div className="flex min-h-screen items-center justify-center"><CircleNotch className="h-6 w-6 animate-spin text-stone-400" /></div>}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/onboarding" element={<Onboarding />} />
              {/* Setup lives on /onboarding now (steps 5–6 of the one journey); old links and bookmarks still land. */}
              <Route path="/organization-setup" element={<Navigate to="/onboarding" replace />} />
              <Route path="/confirm-email" element={<ConfirmEmail />} />
              <Route path="/reset-password" element={<ResetPassword />} />
            </Routes>
          </Suspense>
        </div>
        {/* Toasts must mount on public routes too (e.g. email-confirmed notice on login). */}
        <Toaster />
      </div>
    );
  }

  // Render authenticated app with proper role-based routing
  return (
    <RoleBasedRouter isAuthenticated={isAuthenticated} inOrg={inOrg} orgId={orgId}>
      <Routes>
        <Route path="/" element={<AppLayoutWithSidebar />}>
          <Route index element={<Navigate to="/assistant" replace />} />
          <Route path="assistant" element={<Home />} />
          <Route path="map" element={<Map />} />
          <Route path="service-area" element={<ServiceArea />} />
          <Route path="team" element={<Team />} />
          {/* Redirects from old URLs */}
          <Route path="crews" element={<Navigate to="/team?tab=crews" replace />} />
          <Route path="members" element={<Navigate to="/team?tab=members" replace />} />
          <Route path="services" element={<ServicesManagement />} />
          <Route path="customers" element={<CustomersManagement />} />
          <Route path="service-templates" element={<ServiceTemplates />} />
          <Route path="service-templates/:templateId" element={<ServiceTemplateDetailsPage />} />
          <Route path="service-templates/merge" element={<MergeTemplates />} />
          <Route path="schedule" element={<AllVisits forcedTab="today" />} />
          <Route path="review" element={<Review />} />
          <Route path="visits" element={<AllVisits forcedTab="all" />} />
          <Route path="dispatch" element={<Dispatch />} />
          <Route path="revenue" element={<RevenueReport />} />
          <Route path="revenue/performance" element={<PerformanceReport />} />
          <Route path="revenue/subcontractors" element={<SubcontractorsReport />} />
          <Route path="billing-issues" element={<BillingIssues />} />
          <Route path="visits/review" element={<VisitsRequiringReview />} />
          <Route path="visits/follow-up" element={<VisitsRequiringFollowup />} />
          <Route path="messages" element={<Messages />} />
          <Route path="campaigns" element={<Campaigns />} />
          <Route path="settings/*" element={<Settings />} />
          <Route path="user-settings" element={<UserSettings />} />
          <Route path="data-import" element={<Navigate to="/settings?tab=data" replace />} />
          <Route path="invoices/:invoiceId" element={<InvoiceRouteOverlay />} />
          <Route path="visits/:visitId/invoice" element={<InvoiceRouteOverlay />} />
          <Route
            path="customers/:customerId/subscriptions/:subscriptionId/setup-fee-invoice"
            element={<InvoiceView />}
          />
          <Route
            path="customers/:customerId/ledger-entries/:entryId/invoice"
            element={<InvoiceView />}
          />
          {isAdminRoute && (
            <>
              <Route path="/confirm-email" element={<ConfirmEmail />} />
              {/* Setup lives on /onboarding now (steps 5–6 of the one journey); old links and bookmarks still land. */}
              <Route path="/organization-setup" element={<Navigate to="/onboarding" replace />} />
            </>
          )}
          {/* Canonical entity paths — used in email links, interceptable by mobile universal links */}
          <Route path="customers/:id" element={<CustomerDetails />} />
          <Route path="customers/:customerId/subscriptions/:subscriptionId" element={<SubscriptionRedirect />} />
          <Route path="visits/:visitId" element={<VisitRedirect />} />
          <Route path="crews/:id" element={<EntityPathRedirect />} />
          <Route path="conversations/:id" element={<EntityPathRedirect />} />
          <Route path="rate-requests/:id" element={<EntityPathRedirect />} />
          <Route path="*" element={<Navigate to="/assistant" replace />} />
        </Route>
      </Routes>
    </RoleBasedRouter>
  );
}

function App() {
  // Set up automatic token refresh
  useTokenRefresh();

  const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
  const posthogHost = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

  // Only wrap with PostHogProvider if key is configured
  if (posthogKey) {
    return (
      <PostHogProvider
        apiKey={posthogKey}
        options={{
          api_host: posthogHost,
          person_profiles: 'identified_only',
          // Replay is off app-wide; only signup pages record, masked (RUT-4433).
          ...REPLAY_INIT_OPTIONS,
          loaded: (posthog) => {
            posthog.register(appSuperProperties);
            // A signup page that mounted before init asked for a recording.
            syncOnboardingReplay(posthog);
          },
        }}
      >
        <Router>
          <ThemedApp />
        </Router>
      </PostHogProvider>
    );
  }

  return (
    <Router>
      <ThemedApp />
    </Router>
  );
}

// Wrapper component to handle theme based on authentication
function ThemedApp() {
  const { isAuthenticated } = useAuthStatus();
  const envBanner = useEnvironmentBanner();

  // Initialize PostHog user identification
  usePostHogIdentify();

  // Notify when a newer deploy is available so the user can refresh on their own terms
  useVersionCheck();

  return (
    <ThemeProvider forceLight={!isAuthenticated}>
      <div className="flex flex-col h-screen overflow-clip overscroll-none touch-manipulation">
        {envBanner && (
          <div className={`${envBanner.colors} text-[10px] font-semibold uppercase tracking-widest text-center py-0.5 shrink-0`}>
            {envBanner.label}
          </div>
        )}
        {/* Only while the environment banner is visible, translateZ(0) creates a new
            containing block so descendant `fixed` elements (e.g. the sidebar nav's fixed
            inner wrapper) anchor to this region instead of the viewport, keeping them below
            the banner. It must not be applied otherwise: a transform cancels descendant
            `position: fixed`, letting the left nav pan away with the page (RUT-2632). */}
        <div className="flex-1 min-h-0" style={envBanner ? { transform: 'translateZ(0)' } : undefined}>
          <RightSidebarProvider>
            <TopBarActionsProvider>
              <BreadcrumbProvider>
                <SessionExpiredProvider>
                  <ChatProvider>
                    <RutaAiProvider>
                      <AppContent />
                      <RutaAiPopover />
                      <SetupIntroGate />
                      <SessionExpiredModal />
                    </RutaAiProvider>
                  </ChatProvider>
                </SessionExpiredProvider>
              </BreadcrumbProvider>
            </TopBarActionsProvider>
          </RightSidebarProvider>
        </div>
        <Toaster />
      </div>
    </ThemeProvider>
  );
}

export default App;
